<template>
  <div class="product-page">
    <!-- 顶部步骤导航 -->
    <van-steps :active="2" active-color="#ee0a24">
      <van-step>复制商品链接</van-step>
      <van-step>粘贴至输入框</van-step>
      <van-step>找券享优惠</van-step>
    </van-steps>

    <!-- 搜索区域 -->
    <div class="search-section">
      <van-search
        v-model="searchValue"
        placeholder="搜索商品链接"
        shape="round"
        background="#f7f8fa"
      />
      <div class="clear-search" @click="clearSearch">
        <van-icon name="delete-o" />
        清空搜索
      </div>
      <van-button 
        type="danger" 
        round 
        block 
        class="search-btn"
        @click="handleSearch"
      >
        <van-icon name="coupon-o" />
        找券享优惠
      </van-button>
    </div>


    <!-- 分类导航 -->
    <van-tabs v-model:active="activeTab" color="#ee0a24" sticky>
      <van-tab title="推荐" />
      <van-tab title="母婴" />
      <van-tab title="百货" />
      <van-tab title="好券" />
      <van-tab title="热卖" />
      <van-tab title="美妆" />
    </van-tabs>

    <!-- 商品列表 -->
    <van-list
      v-model:loading="loading"
      :finished="finished"
      finished-text="没有更多了"
      v-model:error="error"
      error-text="请求失败，点击重新加载"
      @load="onLoad"
    >
      <div class="product-list">
        <div 
          class="product-item" 
          v-for="item in productList" 
          :key="item.id"
          @click="goToProduct(item)"
        >
          <div class="product-image">
            <van-image
              :src="getImageUrl(item.image_url)"
              fit="cover"
            >
              <template v-slot:loading>
                <van-loading type="spinner" size="20" />
              </template>
              <template v-slot:error>
                <div class="image-error">图片加载失败</div>
              </template>
            </van-image>
            <!-- 平台标签 -->
            <div class="platform-badge" v-if="item.has_coupon">
              <van-tag type="danger" size="small">选乐</van-tag>
            </div>
          </div>
          <div class="product-info">
            <div class="product-title">
              {{ item.sku_name }}
            </div>
            <!-- 优惠券 -->
            <div class="coupon-tag" v-if="item.has_coupon">
              <span class="coupon-label">{{ (item.coupon_benefit / 10).toFixed(1) }}元券</span>
            </div>
            <!-- 价格信息 -->
            <div class="price-section">
              <div class="price-info">
                <span class="price-label">券后</span>
                <span class="current-price">{{ (item.purchase_price / 10).toFixed(1) }}</span>
                <span class="original-price">{{ (item.wl_price / 10).toFixed(1) }}</span>
              </div>
            </div>
            <!-- 附加信息 -->
            <div class="extra-info">
              <div class="shop-info">
                <van-icon name="shop-o" size="12" />
                <span class="shop-name">{{ item.shop_name }}</span>
              </div>
              <span class="sales-info">
                销量~ {{ formatNumber(item.in_order_count_30_days) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </van-list>

    <!-- 底部导航 -->
    <van-tabbar v-model="activeNav" active-color="#ee0a24">
      <van-tabbar-item icon="home-o">首页</van-tabbar-item>
      <van-tabbar-item icon="star-o">会员卡券</van-tabbar-item>
      <van-tabbar-item icon="search">美团团购</van-tabbar-item>
      <van-tabbar-item icon="chat-o">淘宝团购</van-tabbar-item>
      <van-tabbar-item icon="shopping-cart-o">
        转商购物
        <template #badge>
          <div class="custom-badge"></div>
        </template>
      </van-tabbar-item>
    </van-tabbar>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { getProductList, searchProduct } from '@/api';
import { 
  Button, 
  Search, 
  Steps, 
  Step, 
  Tabs, 
  Tab, 
  List, 
  Image as VanImage, 
  Loading,
  Tag,
  Icon,
  Tabbar,
  TabbarItem,
  showToast,
  showFailToast,
  showDialog,
} from 'vant';

export default {
  name: 'ProductList',
  components: {
    [Button.name]: Button,
    [Search.name]: Search,
    [Steps.name]: Steps,
    [Step.name]: Step,
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [List.name]: List,
    [VanImage.name]: VanImage,
    [Loading.name]: Loading,
    [Tag.name]: Tag,
    [Icon.name]: Icon,
    [Tabbar.name]: Tabbar,
    [TabbarItem.name]: TabbarItem,
  },
  setup() {
    const searchValue = ref('');
    const activeTab = ref(0);
    const activeNav = ref(4);
    const productList = ref([]);
    const loading = ref(false);
    const finished = ref(false);
    const error = ref(false);
    const page = ref(1);
    const pageSize = ref(20);

    // 获取商品列表
    const fetchProducts = async () => {
      try {
        // 如果是error状态重试，需要重置error
        error.value = false;
        // loading.value = true; // van-list 会自动设置 loading 为 true

        const response = await getProductList({
          project: 'discount_website',
          page: page.value,
          page_size: pageSize.value
        });

        if (response.data.code === 200) {
          const newProducts = response.data.data || [];
          productList.value = [...productList.value, ...newProducts];
          
          // 判断是否还有更多数据
          const meta = response.data.meta?.pagination;
          if (meta && page.value >= meta.total_page) {
            finished.value = true;
          }
          
          page.value++;
        } else {
          // 业务错误，设置 error 状态，停止自动加载
          error.value = true;
        }
      } catch (err) {
        console.error('获取商品列表失败:', err);
        // 网络错误，设置 error 状态，停止自动加载
        error.value = true;
      } finally {
        loading.value = false;
      }
    };

    // 加载更多
    const onLoad = () => {
      fetchProducts();
    };

    // 清空搜索
    const clearSearch = () => {
      searchValue.value = '';
    };

    // 搜索
    const handleSearch = async () => {
      if (!searchValue.value.trim()) {
        showToast('请输入搜索内容');
        return;
      }
      
      const toast = showToast({
        message: '转换中...',
        type: 'loading',
        duration: 0,
        forbidClick: true
      });

      try {
        const formData = new FormData();
        formData.append('project', 'discount_website');
        formData.append('keyword', searchValue.value);

        const response = await searchProduct(formData);

        toast.close();

        if (response.data.code === 200) {
          const targetUrl = response.data.data;
          showDialog({
            title: '转链成功',
            message: '点击下方按钮前往优惠页面',
            confirmButtonText: '立即前往',
            showCancelButton: true
          }).then(() => {
            window.open(targetUrl, '_blank');
          }).catch(() => {
            // on cancel
          });
        } else {
          showFailToast(response.data.message || '转换失败');
        }
      } catch (error) {
        toast.close();
        // console.error('搜索失败:', error);
        showFailToast('网络请求失败');
      }
    };

    // 获取图片URL
    const getImageUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('http')) return url;
      return `https://img14.360buyimg.com/n1/${url}`;
    };

    // 格式化数字
    const formatNumber = (num) => {
      if (!num) return 0;
      if (num >= 10000) {
        return (num / 10000).toFixed(1) + '万';
      }
      return num;
    };

    // 跳转到商品详情
    const goToProduct = (item) => {
      if (item.sku_url) {
        window.open(item.sku_url, '_blank');
      }
    };

    onMounted(() => {
      // 初始加载
      // fetchProducts();
    });

    return {
      searchValue,
      activeTab,
      activeNav,
      productList,
      loading,
      finished,
      error,
      onLoad,
      clearSearch,
      handleSearch,
      getImageUrl,
      formatNumber,
      goToProduct
    };
  }
};
</script>

<style scoped>
.product-page {
  background: #f7f8fa;
  min-height: 100vh;
  padding-bottom: 60px;
}

/* 步骤导航 */
:deep(.van-steps--horizontal) {
  padding: 12px 10px;
  background: #fff;
}

:deep(.van-step__title) {
  font-size: 12px;
  color: #646566;
}

:deep(.van-step__circle) {
  width: 16px;
  height: 16px;
}

/* 搜索区域 */
.search-section {
  background: #fff;
  padding: 10px 12px;
  margin-bottom: 1px;
  position: sticky;
  top: 0;
  z-index: 99;
  box-shadow: 0 2px 10px rgba(0,0,0,0.03);
}

/* 搜索框样式优化 */
:deep(.van-search) {
  padding: 0;
  margin-bottom: 10px;
}

:deep(.van-search__content) {
  background: #f7f8fa;
  border-radius: 18px;
}

.clear-search {
  display: flex;
  align-items: center;
  justify-content: flex-end; /* 靠右显示 */
  color: #999;
  font-size: 12px;
  margin: 4px 0 10px;
  cursor: pointer;
  padding: 0 4px;
}

.clear-search .van-icon {
  margin-right: 4px;
  font-size: 14px;
}

.search-btn {
  height: 40px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 20px;
  background: linear-gradient(90deg, #ff6034, #ee0a24);
  border: none;
  box-shadow: 0 4px 10px rgba(238, 10, 36, 0.3); /* 按钮阴影 */
}

.search-btn .van-icon {
  margin-right: 6px;
  font-size: 16px;
}



/* 商品列表 - 双列布局优化 */
.product-list {
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  box-sizing: border-box;
}

.product-item {
  width: 48.5%; /* 双列宽度 */
  display: flex;
  flex-direction: column; /* 垂直排列 */
  background: #fff;
  border-radius: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  overflow: hidden; /* 确保图片圆角不溢出 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); /* 柔和阴影 */
  transition: all 0.3s;
}

.product-item:active {
  transform: scale(0.98);
}

.product-image {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* 1:1 正方形图片 */
  margin-right: 0;
  border-radius: 0;
  overflow: hidden;
}

.product-image .van-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.image-error {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f7f8fa;
  color: #969799;
  font-size: 12px;
}

.platform-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 10px 12px; /* 内容区域内边距 */
}

.product-title {
  font-size: 13px;
  color: #333;
  line-height: 1.4;
  height: 36px; /* 限制两行高度 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  margin-bottom: 8px;
  font-weight: 500;
}

.coupon-tag {
  margin-bottom: 8px;
  display: flex;
}

.coupon-label {
  display: inline-flex;
  align-items: center;
  height: 18px;
  background: linear-gradient(90deg, #ff6034, #ee0a24); /* 渐变背景 */
  color: #fff;
  font-size: 10px;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 500;
  position: relative;
}

/* 给优惠券加一点装饰（可选） */
.coupon-label::before,
.coupon-label::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 4px;
  height: 4px;
  background: #fff;
  border-radius: 50%;
  transform: translateY(-50%);
}
.coupon-label::before { left: -2px; }
.coupon-label::after { right: -2px; }

.price-section {
  margin-bottom: 6px;
  margin-top: auto; /* 将价格推到底部 */
}

.price-info {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}

.price-label {
  font-size: 11px;
  color: #ee0a24;
  margin-right: 2px;
}

.current-price {
  font-size: 18px;
  color: #ee0a24;
  font-weight: 600;
  margin-right: 6px;
  line-height: 1;
}

.current-price::before {
  content: '¥';
  font-size: 12px;
  margin-right: 1px;
}

.original-price {
  font-size: 11px;
  color: #bdc3c7;
  text-decoration: line-through;
}

.original-price::before {
  content: '¥';
}

.extra-info {
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.shop-info {
  display: flex;
  align-items: center;
  flex: 1;
  overflow: hidden;
  max-width: 60%;
}

.shop-info .van-icon {
  margin-right: 2px;
  font-size: 11px;
}

.shop-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sales-info {
  flex-shrink: 0;
  color: #999;
}

/* 分类标签样式 */
:deep(.van-tabs__wrap) {
  background: #fff;
}

:deep(.van-tab) {
  font-size: 15px;
  padding: 0 12px;
}

:deep(.van-tab--active) {
  font-weight: 600;
}

/* 底部导航 */
.custom-badge {
  position: absolute;
  top: 0;
  right: 10px;
  width: 8px;
  height: 8px;
  background: #ee0a24;
  border-radius: 50%;
}

:deep(.van-tabbar-item__text) {
  font-size: 12px;
}
</style>

